// Tasks.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Add } from "iconsax-reactjs";
import apiService from "../../services/api";
import { toast } from "react-hot-toast";
import { useTheme } from "../../contexts/ThemeContext";
import CreateTaskModal from "../../components/CreateTaskModal";

const Tasks = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    myTasks: 0,
    overdue: 0,
    dueToday: 0,
    completionRate: 0,
  });
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [filters, setFilters] = useState({ status: "", priority: "" });

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Only fetch tasks created by the current user
      const [tasksResponse, statsResponse] = await Promise.all([
        apiService.getTasks({ ...filters, myTasks: true }),
        apiService.getTaskStats(),
      ]);
      setTasks(tasksResponse.data || []);
      setStats(statsResponse.data || {});
    } catch (error) {
      console.error("Error loading tasks:", error);
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    todo: "bg-gray-600 text-gray-200",
    in_progress: "bg-blue-600 text-white",
    review: "bg-yellow-600 text-white",
    completed: "bg-green-600 text-white",
    blocked: "bg-red-600 text-white",
  };

  const priorityColors = {
    low: "text-gray-400",
    medium: "text-yellow-400",
    high: "text-orange-400",
    urgent: "text-red-400",
  };

  const StatCard = ({ title, value }) => (
    <div
      className={`rounded-xl p-6 border transition-colors ${
        isDarkMode
          ? "bg-[#0C0E12] border-gray-700/50"
          : "bg-white border-gray-200 shadow-sm"
      }`}
    >
      <div
        className={`text-[14px] font-medium mb-3 ${
          isDarkMode ? "text-[#94979C]" : "text-gray-500"
        }`}
      >
        {title}
      </div>
      <div
        className={`text-[28px] font-bold ${
          isDarkMode ? "text-[#F7F7F7]" : "text-gray-900"
        }`}
      >
        {value}
      </div>
    </div>
  );

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${
        isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-[#F4F5F7] text-gray-900"
      }`}
    >
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1
              className={`text-3xl font-bold mb-1 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              My Tasks
            </h1>
            <p
              className={`text-sm ${
                isDarkMode ? "text-[#94979C]" : "text-gray-500"
              }`}
            >
              Tasks created by or assigned to you.
            </p>
          </div>

          <button
            onClick={() => setShowNewTaskModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-lg font-medium transition-all duration-300 hover:scale-[1.02] hover:shadow-xl text-sm shrink-0"
            style={{
              background:
                "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
              boxShadow: `
                0px 36px 32px -16px #427BCA36,
                0px 8px 16px 0px #2F578F1F,
                0px 8px 24px -16px #FFFFFF3D inset,
                0px -24px 32px 0px #FFFFFF38 inset,
                0px 0px 12px 0px #FFFFFF inset
              `,
            }}
          >
            <Add size={16} className="shrink-0" />
            <span>New Task</span>
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard title="My Tasks" value={stats.myTasks ?? 0} />
          <StatCard title="Overdue" value={stats.overdue ?? 0} />
          <StatCard title="Due Today" value={stats.dueToday ?? 0} />
          <StatCard
            title="Completion Rate"
            value={`${stats.completionRate ?? 0}%`}
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              isDarkMode
                ? "bg-[#0C0E12] border border-gray-700 text-[#94979C] hover:text-white hover:border-gray-600"
                : "bg-white border border-gray-300 text-gray-500 hover:text-gray-700"
            }`}
          >
            <option value="">All Status</option>
            <option value="todo">To Do</option>
            <option value="in_progress">In Progress</option>
            <option value="review">Review</option>
            <option value="completed">Completed</option>
            <option value="blocked">Blocked</option>
          </select>

          <select
            value={filters.priority}
            onChange={(e) =>
              setFilters({ ...filters, priority: e.target.value })
            }
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              isDarkMode
                ? "bg-[#0C0E12] border border-gray-700 text-[#94979C] hover:text-white hover:border-gray-600"
                : "bg-white border border-gray-300 text-gray-500 hover:text-gray-700"
            }`}
          >
            <option value="">All Priority</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>

        {/* Tasks Table */}
        <div
          className={`rounded-xl border transition-colors ${
            isDarkMode
              ? "bg-[#0C0E12] border-gray-700/50"
              : "bg-white border-gray-200 shadow-sm"
          }`}
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className={isDarkMode ? "bg-[#13161B]" : "bg-gray-50"}>
                <tr
                  className={`border-b ${
                    isDarkMode ? "border-[#22262F]" : "border-gray-100"
                  }`}
                >
                  {["Task", "Status", "Priority", "Due Date"].map((col) => (
                    <th
                      key={col}
                      className={`text-left py-4 px-4 font-medium text-sm ${
                        isDarkMode ? "text-[#94979C]" : "text-gray-500"
                      }`}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="4"
                      className={`py-10 text-center text-sm ${
                        isDarkMode ? "text-[#94979C]" : "text-gray-500"
                      }`}
                    >
                      Loading tasks...
                    </td>
                  </tr>
                ) : tasks.length === 0 ? (
                  <tr>
                    <td
                      colSpan="4"
                      className={`py-10 text-center text-sm ${
                        isDarkMode ? "text-[#94979C]" : "text-gray-500"
                      }`}
                    >
                      No tasks found. Create your first task above.
                    </td>
                  </tr>
                ) : (
                  tasks.map((task) => (
                    <tr
                      key={task.id}
                      onClick={() => navigate(`/tasks/${task.id}`)}
                      className={`border-b cursor-pointer transition-colors ${
                        isDarkMode
                          ? "border-[#22262F] hover:bg-gray-800/30"
                          : "border-gray-100 hover:bg-gray-50"
                      }`}
                    >
                      {/* Task title + description */}
                      <td className="py-4 px-4">
                        <div
                          className={`font-medium text-[15px] ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {task.title}
                        </div>
                        {task.description && (
                          <div
                            className={`text-[13px] mt-0.5 ${
                              isDarkMode ? "text-[#94979C]" : "text-gray-500"
                            }`}
                          >
                            {task.description.substring(0, 60)}
                            {task.description.length > 60 ? "…" : ""}
                          </div>
                        )}
                      </td>

                      {/* Status */}
                      <td className="py-4 px-4">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                            statusColors[task.status]
                          }`}
                        >
                          {task.status.replace("_", " ").toUpperCase()}
                        </span>
                      </td>

                      {/* Priority */}
                      <td className="py-4 px-4">
                        <span
                          className={`text-sm font-medium ${
                            priorityColors[task.priority]
                          }`}
                        >
                          {task.priority.toUpperCase()}
                        </span>
                      </td>

                      {/* Due date */}
                      <td
                        className={`py-4 px-4 text-[14px] ${
                          isDarkMode ? "text-[#94979C]" : "text-gray-600"
                        }`}
                      >
                        {task.dueDate
                          ? new Date(task.dueDate).toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })
                          : "No due date"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showNewTaskModal && (
        <CreateTaskModal
          onClose={() => setShowNewTaskModal(false)}
          onSuccess={() => {
            setShowNewTaskModal(false);
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default Tasks;
